import React from "react";
import { motion } from "framer-motion";
import { IMAGE_URL } from "../../api-urls";
import icons from "../../icons.config";
import { setClickedItem } from "../../store/itemsSlice"; // Import setClickedItem directly

const RelatedProducts = ({
  itemData,
  getVisibleDresses,
  isFading,
  handleNext,
  handlePrev,
  handleDotClick,
  visibleItems,
  currentIndex,
  dispatch,
  navigate,
  likedItems,
  handleHeartClick,
  handlers,
}) => {
  // Helper function to construct URL if item.url is missing
  const getProductUrl = (item) => {
    if (item.url && typeof item.url === "string" && item.url.trim() !== "") {
      return item.url.startsWith("/") ? item.url : `/${item.url}`; // Ensure absolute path
    }
    // Fallback: Construct URL dynamically
    const formatText = (text) => encodeURIComponent(text.replace(/\s+/g, ""));
    const category = formatText(item.product_category?.[0]?.toLowerCase() || "products");
    const productName = formatText(item.product_name || "product");
    const variant = formatText(item.default_variant?.title || "variant");
    const variantId = item.default_variant?.id || "0";
    return `/${category}/${productName}/${variant}/${variantId}/buy`;
  };

  return (
    <div className="flex flex-col justify-center items-center w-full bg-tertiary pt-10 lg:pt-16 pb-10 xl:pb-0 relative space-y-5 overflow-y-auto overflow-x-hidden">
      <h1 className="text-primary1 font-poppins text-3xl lg:py-8 mt-4 font-extralight mb-6">YOU MAY ALSO LIKE</h1>

      {/* Mobile Doodle */}
      <div className="lg:hidden absolute -right-7 top-[20%] overflow-hidden">
        <img src={icons.viewDressCirclesM} alt="Doodle Left" className="w-32 md:w-48 max-w-full" />
      </div>

      {/* Desktop Doodle */}
      <div className="hidden lg:flex absolute right-0 top-[20%] overflow-hidden">
        <img src={icons.viewDressCircles2} alt="Doodle Left" className="w-48 xl:w-52 max-w-full" />
      </div>

      <div className="relative w-full h-full lg:max-w-[82%] xl:max-w-[83%] 2xl:max-w-[84%] lg:h-auto" {...handlers}>
        <img
          src={icons.arrowLeft}
          alt="left arrow"
          className="absolute lg:left-7 xl:-left-1 top-[40%] transform -translate-y-1/2 text-primary1 text-lg font-semibold w-8 h-8 rounded-[50%] z-20 bg-secondary hidden lg:block cursor-pointer"
          onClick={handlePrev}
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10 lg:gap-6 px-4 sm:px-6 md:px-8 lg:px-11 xl:px-2 h-full overflow-x-hidden">
          {getVisibleDresses().map((item) => (
            <div
              key={item.id}
              className="text-left relative z-10 flex flex-col w-full h-[250px] md:h-[350px] lg:h-[500px] xl:h-[600px] 2xl:h-[600px]"
            >
              <motion.div
                key={item.id}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.5 }}
                className={`w-full h-full flex items-center justify-center transition-opacity duration-500 ${
                  isFading ? "opacity-0" : "opacity-100"
                }`}
              >
                <div className="relative w-full h-full">
                  <motion.img
                    src={IMAGE_URL + item.default_variant.variant_main_image}
                    alt={item.default_variant.title}
                    className="w-full h-full object-cover cursor-pointer"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 2.5 }}
                    onClick={() => {
                      dispatch(setClickedItem(item));
                      const productUrl = getProductUrl(item);
                      navigate(productUrl);
                    }}
                  />
                  <div
                    className="absolute top-4 right-4 cursor-pointer z-20"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeartClick(item);
                    }}
                  >
                    <img
                      src={
                        likedItems.some(
                          (likedItem) =>
                            likedItem?.variant_id?.toString() === item?.default_variant?.id?.toString()
                        )
                          ? icons.filledHeart
                          : icons.heart
                      }
                      alt="Heart Icon"
                      className="w-5 h-5 md:w-7 md:h-7"
                    />
                  </div>
                </div>
              </motion.div>
              <motion.h2
                className="font-light mt-2 font-outfit text-xl text-primary1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5 }}
              >
                {item.product_name}
              </motion.h2>
              <motion.p
                className="text-sm mt-1 text-hcolor font-poppins font-light"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5 }}
              >
                {item.default_variant.discount_price}
              </motion.p>
            </div>
          ))}
        </div>
        <img
          src={icons.arrowRight}
          alt="right arrow"
          className="absolute top-[40%] lg:right-7 xl:-right-2 transform -translate-y-1/2 hidden text-primary1 rounded-[50%] bg-secondary lg:block w-8 h-8 z-10 cursor-pointer"
          onClick={handleNext}
        />
      </div>

      <div className="flex justify-center pt-8 lg:hidden relative top-7">
        {Array.from({ length: Math.ceil(itemData.length / visibleItems) }).map((_, dotIndex) => (
          <button
            key={dotIndex}
            onClick={() => handleDotClick(dotIndex)}
            className={`w-2 h-2 md:h-3 md:w-3 mx-2 rounded-full ${
              dotIndex === Math.floor(currentIndex / visibleItems)
                ? "bg-hcolor"
                : "bg-white border border-hcolor"
            } shadow-lg`}
          />
        ))}
      </div>

      <div className="lg:hidden absolute right-0 bottom-16 overflow-hidden">
        <img src={icons.collectionMFlower} alt="Doodle Right" className="w-16 md:w-32 max-w-full" />
      </div>
    </div>
  );
};

export default RelatedProducts;